import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, AlertCircle, CheckCircle2, Files, Trash2 } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Conversion configurations
export const CONVERSIONS = {
  'pdf-to-docx': {
    title: 'PDF to DOCX',
    subtitle: 'Convert PDF to Word',
    description: 'Convert your PDF documents to editable Microsoft Word DOCX files. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/docx',
    outputExt: 'docx',
    icon: 'word',
    color: 'blue'
  },
  'pdf-to-pptx': {
    title: 'PDF to PPTX',
    subtitle: 'Convert PDF to PowerPoint',
    description: 'Convert your PDF documents to Microsoft PowerPoint presentations. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/pptx',
    outputExt: 'pptx',
    icon: 'powerpoint',
    color: 'orange'
  },
  'pdf-to-xlsx': {
    title: 'PDF to XLSX',
    subtitle: 'Convert PDF to Excel',
    description: 'Convert your PDF documents to Microsoft Excel spreadsheets. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/xlsx',
    outputExt: 'xlsx',
    icon: 'excel',
    color: 'green'
  },
  'pdf-to-odt': {
    title: 'PDF to ODT',
    subtitle: 'Convert PDF to OpenDocument',
    description: 'Convert your PDF documents to OpenDocument Text format. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/odt',
    outputExt: 'odt',
    icon: 'document',
    color: 'cyan'
  },
  'pdf-to-rtf': {
    title: 'PDF to RTF',
    subtitle: 'Convert PDF to Rich Text',
    description: 'Convert your PDF documents to Rich Text Format for universal compatibility. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/rtf',
    outputExt: 'rtf',
    icon: 'text',
    color: 'purple'
  },
  'pdf-to-txt': {
    title: 'PDF to Text',
    subtitle: 'Extract Text from PDF',
    description: 'Extract plain text content from your PDF documents. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/txt',
    outputExt: 'txt',
    icon: 'text',
    color: 'slate'
  },
  'pdf-to-html': {
    title: 'PDF to HTML',
    subtitle: 'Convert PDF to Web Page',
    description: 'Convert your PDF documents to HTML web pages. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/html',
    outputExt: 'html',
    icon: 'html',
    color: 'red'
  },
  'pdf-to-epub': {
    title: 'PDF to EPUB',
    subtitle: 'Convert PDF to E-Book',
    description: 'Convert your PDF documents to EPUB e-book format. Supports batch conversion!',
    endpoint: '/api/pdf-convert/convert/epub',
    outputExt: 'epub',
    icon: 'book',
    color: 'amber'
  },
  'pdf-to-pdfa': {
    title: 'PDF to PDF/A',
    subtitle: 'Create Archival PDF',
    description: 'Convert your PDF to PDF/A format for long-term archiving. Supports batch conversion!',
    endpoint: '/api/pdf-convert/to-pdfa',
    outputExt: 'pdf',
    icon: 'archive',
    color: 'indigo'
  }
};

const colorClasses = {
  blue: { bg: 'from-blue-500 to-blue-600', light: 'from-blue-50 to-blue-100', text: 'text-blue-500' },
  orange: { bg: 'from-orange-500 to-orange-600', light: 'from-orange-50 to-amber-50', text: 'text-orange-500' },
  green: { bg: 'from-green-500 to-green-600', light: 'from-green-50 to-emerald-50', text: 'text-green-500' },
  cyan: { bg: 'from-cyan-500 to-cyan-600', light: 'from-cyan-50 to-teal-50', text: 'text-cyan-500' },
  purple: { bg: 'from-purple-500 to-purple-600', light: 'from-purple-50 to-violet-50', text: 'text-purple-500' },
  slate: { bg: 'from-slate-500 to-slate-600', light: 'from-slate-50 to-gray-100', text: 'text-slate-500' },
  red: { bg: 'from-red-500 to-red-600', light: 'from-red-50 to-rose-50', text: 'text-red-500' },
  amber: { bg: 'from-amber-500 to-amber-600', light: 'from-amber-50 to-yellow-50', text: 'text-amber-500' },
  indigo: { bg: 'from-indigo-500 to-indigo-600', light: 'from-indigo-50 to-violet-50', text: 'text-indigo-500' },
};

export function PdfConvertTool({ conversionKey }) {
  const config = CONVERSIONS[conversionKey];
  const colors = colorClasses[config.color];
  
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = `${config.title} - ${config.subtitle} - KayTool`;
  }, [config]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const pdfFiles = selectedFiles.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      toast.error("Please select valid PDF files");
      return;
    }
    
    if (pdfFiles.length < selectedFiles.length) {
      toast.warning(`${selectedFiles.length - pdfFiles.length} non-PDF files were skipped`);
    }
    
    setFiles(prev => [...prev, ...pdfFiles.map(f => ({ file: f, status: 'pending' }))]);
    setConvertedFiles([]);
    setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfFiles = droppedFiles.filter(f => f.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      toast.error("Please drop valid PDF files");
      return;
    }
    
    if (pdfFiles.length < droppedFiles.length) {
      toast.warning(`${droppedFiles.length - pdfFiles.length} non-PDF files were skipped`);
    }
    
    setFiles(prev => [...prev, ...pdfFiles.map(f => ({ file: f, status: 'pending' }))]);
    setConvertedFiles([]);
    setError("");
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setConvertedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setConvertedFiles([]);
    setError("");
    setCurrentFileIndex(0);
  };

  const convertSingleFile = async (fileObj, index) => {
    const formData = new FormData();
    formData.append('file', fileObj.file);
    
    const response = await fetch(`${API_URL}${config.endpoint}`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Conversion failed');
    }
    
    const blob = await response.blob();
    const baseName = fileObj.file.name.replace('.pdf', '');
    const outputName = config.outputExt === 'pdf' 
      ? `${baseName}_pdfa.pdf` 
      : `${baseName}.${config.outputExt}`;
    
    return { blob, name: outputName, originalName: fileObj.file.name };
  };

  const convertAllPdfs = async () => {
    if (files.length === 0) {
      toast.error("Please select PDF files first");
      return;
    }

    setIsProcessing(true);
    setError("");
    setConvertedFiles([]);
    
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      
      // Update file status to processing
      setFiles(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'processing' } : f
      ));
      
      try {
        const result = await convertSingleFile(files[i], i);
        results.push(result);
        
        // Update file status to done
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'done' } : f
        ));
      } catch (err) {
        console.error(`Error converting ${files[i].file.name}:`, err);
        
        // Update file status to error
        setFiles(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'error', errorMsg: err.message } : f
        ));
        
        results.push({ error: err.message, originalName: files[i].file.name });
      }
    }
    
    setConvertedFiles(results);
    setIsProcessing(false);
    
    const successCount = results.filter(r => !r.error).length;
    const errorCount = results.filter(r => r.error).length;
    
    if (successCount === files.length) {
      toast.success(`All ${successCount} files converted successfully!`);
    } else if (successCount > 0) {
      toast.warning(`${successCount} converted, ${errorCount} failed`);
    } else {
      toast.error("All conversions failed");
    }
  };

  const downloadSingle = (converted) => {
    if (!converted.blob) return;
    const url = URL.createObjectURL(converted.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = converted.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadAllAsZip = async () => {
    const successfulFiles = convertedFiles.filter(f => !f.error);
    if (successfulFiles.length === 0) {
      toast.error("No files to download");
      return;
    }
    
    if (successfulFiles.length === 1) {
      downloadSingle(successfulFiles[0]);
      toast.success("File downloaded!");
      return;
    }
    
    const zip = new JSZip();
    
    for (const file of successfulFiles) {
      zip.file(file.name, file.blob);
    }
    
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `converted_${config.outputExt}_files.zip`);
    toast.success(`Downloaded ${successfulFiles.length} files as ZIP!`);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'done':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  const overallProgress = files.length > 0 
    ? Math.round((files.filter(f => f.status === 'done' || f.status === 'error').length / files.length) * 100)
    : 0;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : `bg-gradient-to-br ${colors.light}`}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Files className="w-3 h-3" /> Batch
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colors.bg} text-white rounded-full mb-4`}>
            <FileText className="w-5 h-5" />
            <span className="font-medium">{config.title}</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            {config.subtitle}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {config.description}
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-6">
          {/* Upload Area */}
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? `border-${config.color}-500 bg-${config.color}-50 dark:bg-${config.color}-900/20` : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="pdf-file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${colors.text}`} />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDFs or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select multiple PDF files to convert to {config.outputExt.toUpperCase()}
                </p>
                <Badge variant="secondary" className="mt-3">
                  <Files className="w-3 h-3 mr-1" /> Batch conversion supported
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* File List */}
          {files.length > 0 && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold dark:text-white flex items-center gap-2">
                    <Files className={`w-5 h-5 ${colors.text}`} />
                    {files.length} PDF{files.length > 1 ? 's' : ''} selected
                  </h3>
                  <Button variant="ghost" size="sm" onClick={clearAll} className="text-red-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                </div>
                
                {/* Progress Bar */}
                {isProcessing && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-600 dark:text-slate-300">
                        Converting {currentFileIndex + 1} of {files.length}...
                      </span>
                      <span className="text-slate-500">{overallProgress}%</span>
                    </div>
                    <Progress value={overallProgress} className="h-2" />
                  </div>
                )}
                
                {/* File Items */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {files.map((fileObj, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        fileObj.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20' :
                        fileObj.status === 'done' ? 'bg-green-50 dark:bg-green-900/20' :
                        fileObj.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                        'bg-slate-50 dark:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {getStatusIcon(fileObj.status)}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate dark:text-white">
                            {fileObj.file.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatSize(fileObj.file.size)}
                            {fileObj.errorMsg && (
                              <span className="text-red-500 ml-2">{fileObj.errorMsg}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {convertedFiles[index] && !convertedFiles[index].error && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => downloadSingle(convertedFiles[index])}
                            data-testid={`download-single-${index}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {!isProcessing && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => removeFile(index)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && (
            <Card className="dark:bg-slate-800 border-red-200 dark:border-red-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Convert Button */}
          {files.length > 0 && !isProcessing && convertedFiles.length === 0 && (
            <div className="text-center">
              <Button 
                onClick={convertAllPdfs}
                size="lg"
                className={`bg-gradient-to-r ${colors.bg} hover:opacity-90`}
                data-testid="convert-all-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Convert {files.length} PDF{files.length > 1 ? 's' : ''} to {config.outputExt.toUpperCase()}
              </Button>
            </div>
          )}

          {/* Success - Download All */}
          {convertedFiles.length > 0 && convertedFiles.some(f => !f.error) && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-slate-800 dark:text-white mb-2">
                  Conversion Complete!
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {convertedFiles.filter(f => !f.error).length} of {files.length} files converted to {config.outputExt.toUpperCase()}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={downloadAllAsZip}
                    className="bg-gradient-to-r from-green-500 to-green-600"
                    data-testid="download-all-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {convertedFiles.filter(f => !f.error).length > 1 
                      ? 'Download All (ZIP)' 
                      : `Download ${config.outputExt.toUpperCase()}`
                    }
                  </Button>
                  <Button variant="outline" onClick={clearAll}>
                    Convert More
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Secure batch file conversion.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

// Individual page exports
export function PdfToDocx() { return <PdfConvertTool conversionKey="pdf-to-docx" />; }
export function PdfToPptx() { return <PdfConvertTool conversionKey="pdf-to-pptx" />; }
export function PdfToXlsx() { return <PdfConvertTool conversionKey="pdf-to-xlsx" />; }
export function PdfToOdt() { return <PdfConvertTool conversionKey="pdf-to-odt" />; }
export function PdfToRtf() { return <PdfConvertTool conversionKey="pdf-to-rtf" />; }
export function PdfToTxt() { return <PdfConvertTool conversionKey="pdf-to-txt" />; }
export function PdfToHtml() { return <PdfConvertTool conversionKey="pdf-to-html" />; }
export function PdfToEpub() { return <PdfConvertTool conversionKey="pdf-to-epub" />; }
export function PdfToPdfa() { return <PdfConvertTool conversionKey="pdf-to-pdfa" />; }
